import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import type { SepEnvironment } from '@/types/api';

import { currentEnvironment, subscribeToEnvironment, syncEnvironmentToUrl } from './store';

const EnvironmentContext = createContext<SepEnvironment>(currentEnvironment());

/**
 * Bridges the module-level environment store into React.
 *
 * The store is the source of truth rather than this state, because `api/client`
 * has to read the environment on every request and is not a component. This
 * subscribes so the tree re-renders when it changes, and does nothing else.
 */
export function EnvironmentProvider({ children }: { children: ReactNode }) {
  const [environment, setLocal] = useState<SepEnvironment>(currentEnvironment);

  useEffect(() => {
    // Reflect the initial selection into the URL, so a tab restored from
    // localStorage still carries its environment in a link someone might copy.
    syncEnvironmentToUrl();
    return subscribeToEnvironment(setLocal);
  }, []);

  return (
    <EnvironmentContext.Provider value={environment}>{children}</EnvironmentContext.Provider>
  );
}

export function useEnvironment(): SepEnvironment {
  return useContext(EnvironmentContext);
}
